import { format, parseISO, differenceInDays } from 'date-fns'
import { es } from 'date-fns/locale'

export * from './phone'

// Date formatting
export function formatDate(date: string | Date, formatStr = 'dd/MM/yyyy'): string {
  const d = typeof date === 'string' ? parseISO(date) : date
  return format(d, formatStr, { locale: es })
}

export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date
  return format(d, 'dd/MM/yyyy HH:mm', { locale: es })
}

export function formatDateLong(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date
  return format(d, "d 'de' MMMM 'de' yyyy", { locale: es })
}

// Calculate nights between dates
export function calculateNights(checkIn: string | Date, checkOut: string | Date): number {
  const start = typeof checkIn === 'string' ? parseISO(checkIn) : checkIn
  const end = typeof checkOut === 'string' ? parseISO(checkOut) : checkOut
  return differenceInDays(end, start)
}

// Currency formatting (Honduran Lempira)
export function formatCurrency(amount: number | string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  return new Intl.NumberFormat('es-HN', {
    style: 'currency',
    currency: 'HNL',
    minimumFractionDigits: 2,
  }).format(num)
}

// Status labels
export const reservationStatusLabels: Record<string, string> = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  checked_in: 'En Estadía',
  checked_out: 'Finalizada',
  cancelled: 'Cancelada',
  no_show: 'No Show',
}

export const roomStatusLabels: Record<string, string> = {
  available: 'Disponible',
  occupied: 'Ocupada',
  maintenance: 'Mantenimiento',
  blocked: 'Bloqueada',
}

export const cleaningStatusLabels: Record<string, string> = {
  clean: 'Limpia',
  dirty: 'Sucia',
  in_progress: 'En Limpieza',
}

export const paymentMethodLabels: Record<string, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  card_pos: 'POS Normal',
  card_click: 'Compra Click',
  transfer: 'Transferencia',
  check: 'Cheque',
  credit: 'Crédito',
  other: 'Otro',
}

// Expense status labels
export const expenseStatusLabels: Record<string, string> = {
  draft: 'Borrador',
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  paid: 'Pagado',
  cancelled: 'Cancelado',
}

// Expense payment method labels
export const expensePaymentMethodLabels: Record<string, string> = {
  cash: 'Efectivo',
  bank_transfer: 'Transferencia',
  check: 'Cheque',
  credit_card: 'Tarjeta Crédito',
  petty_cash: 'Caja Chica',
}

// Status colors for badges
export const reservationStatusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  checked_in: 'bg-green-100 text-green-800',
  checked_out: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  no_show: 'bg-purple-100 text-purple-800',
}

export const roomStatusColors: Record<string, string> = {
  available: 'bg-green-100 text-green-800',
  occupied: 'bg-blue-100 text-blue-800',
  maintenance: 'bg-orange-100 text-orange-800',
  blocked: 'bg-red-100 text-red-800',
}

export const cleaningStatusColors: Record<string, string> = {
  clean: 'bg-emerald-100 text-emerald-800',
  dirty: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
}

// Generate initials from name
export function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0) || ''
  const last = lastName?.charAt(0) || ''
  return (first + last).toUpperCase() || 'U'
}

// Phone formatting
export function formatPhone(phone: string): string {
  // Format for Honduras: #### - ####
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 8) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`
  }
  return phone
}
